import React, { useEffect, useState } from 'react';
import Joi from 'joi';
import axios from 'axios';
import { joiResolver } from '@hookform/resolvers/joi';
import { useForm, FormProvider } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { _ } from '_locales';
import { eventTracker } from '_helpers';
import { WaitScreen } from '_components';

import { colors } from '_styles';
import ReminderSuccessScreen from './ReminderSuccessScreen';
import { email, phone } from '../../../helpers/validators';
import { ReminderTime, ReminderCommMethod, ErrorText } from '../components';
import apis from '../../../app/apis';
import HOC from '../../HOC';
import provideRecaptchaToken from '../helpers/recaptchaToken';
import requestPin from '../helpers/requestPin';
import Verification from '../components/Verification';
import ServerError from '../helpers/errors';
import SubscribeToNotifications from './SubscribeToNotifications';
import { convertPhoneNumberToDigits } from '../../BulkPickup/heplers';
import { cts_secure } from '../../../app/cts';

const Container = styled.div`
  margin-top: 20px;
  padding: 0 15px;
  box-sizing: border-box;
  font-size: 12px !important;
`;

const ErrorContainer = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const Button = styled.button`
  font-size: 14px !important;
  padding: 20px 0;
  background-color: ${colors.RC_ACTION};
  cursor: pointer;
  border: none;
  border-radius: 8px;
  color: #fff;
  width: 160px;
  height: 39px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-transform: capitalize;
  margin: 15px auto 50px;

  ${(props) => props.disabled && css`
    cursor: not-allowed;
    background-color: ${colors.GREY}
  `}
`;

const ButtonContainer = styled.div`
  text-align: center;
  margin: 50px 0 50px 0;
`;

const schema = Joi.object({
  method: Joi.string()
    .required()
    .valid('email', 'sms', 'phone')
    .default('email')
    .messages({
      'any.only': _('reminder.please_select_one_of_reminding_methods')
    }),
  email: Joi.alternatives()
    .conditional('method',
      {
        is: 'email',
        then: email(),
        otherwise: Joi.any()
      }),
  phone: Joi.alternatives()
    .conditional('method',
      {
        is: 'phone',
        then: phone(_('general.phone')),
        otherwise: Joi.any()
      }),
  sms: Joi.alternatives()
    .conditional('method',
      {
        is: 'sms',
        then: phone(_('general.mobile')),
        otherwise: Joi.any()
      }),
  areaCode: Joi.string(),
  day: Joi.string()
    .required()
    .messages({
      'string.empty': _('reminder.please_select_the_day_of_week'),
      'string.base': _('reminder.please_select_the_day_of_week'),
    }),
  hour: Joi.string().required(),
  subscribe_to_notifications: Joi.boolean(),
  // pin: Joi.number().required().messages({ 'any.required': _('reminder.pin_is_required') }),
});
const WeeklyReminder = ({
  plugin, config, zone,
}) => {
  const [formData, setFormData] = useState({
    day: '',
    hour: '18:00:00',
    method: 'email',
    email: '',
    sms: '',
    phone: '',
    areaCode: '1',
    subscribe_to_notifications: true
  });

  const [formattedPhone, setFormattedPhone] = useState('');
  const [formattedSms, setFormattedSms] = useState('');

  const unformattedSms = convertPhoneNumberToDigits(formattedSms);
  const unformattedPhone = convertPhoneNumberToDigits(formattedPhone);

  const methods = useForm({
    resolver: joiResolver(schema),
    defaultValues: formData
  });

  const { trigger, getValues, reset, } = methods;
  const PAGE_STATE = { FORM: 'form', SUCCESS: 'success' };
  const FORM_STATE = { INPUT: 'input', VERIFY: 'verify' };

  const [page, setPage] = useState(PAGE_STATE.FORM);
  const [formState, setFormState] = useState(FORM_STATE.INPUT);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const { projectId: project_id } = plugin;
  const { districtId: district_id } = config;
  const { zoneStr: zone_id, fullAddress, mapCityName } = zone;
  const {
    reminderOption: {
      has_phone_reminder,
      has_sms_reminder,
    },

  } = config;

  useEffect(() => {
    reset(formData);
    setErrorMsg('');
  }, [formState]);

  const onNext = async (e) => {
    // After the user enter the email address/phone number and hit the "Create Reminder" button
    e.preventDefault();
    const isValid = await trigger();

    if (!isValid) {
      return;
    }

    if ((getValues().method === 'sms' && unformattedSms.length !== 10)
      || (getValues().method === 'phone' && unformattedPhone.length !== 10)
    ) {
      setErrorMsg('Phone number is not valid!');
      return;
    }

    setLoading(true);
    try {
      if (await requestPin({
        ...getValues(),
        sms: unformattedSms,
        phone: unformattedPhone
      })) {
        setFormData({
          ...formData,
          ...getValues(),
          sms: unformattedSms,
          phone: unformattedPhone
        });
        setFormState(FORM_STATE.VERIFY);
      } else {
        setErrorMsg('An error occurred. We couldn\'t verify your email/number');
      }
    } catch (error) {
      if (error.isAxiosError && error.response.status === 400) {
        setErrorMsg('Seems like you entered invalid data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    const {
      day, hour, pin, email, areaCode, method, subscribe_to_notifications
    } = { ...formData, ...getValues() };
    if (!pin) {
      setErrorMsg('Please enter the 6-digit code that is sent to you.');
      return;
    }
    setLoading(true);
    // generate new token for every request
    const token = await provideRecaptchaToken();
    eventTracker(`Set Reminder - ${method}`, '');
    const payload = {
      project_id,
      district_id,
      zone_id,
      day,
      hour,
      pin,
      token,
      ...(method === 'phone' && ({ country_cd: areaCode, phone_num: unformattedPhone })),
      ...(method === 'sms' && ({ country_cd: areaCode, sms_num: unformattedSms })),
      ...(method === 'email' && ({ email })),
      subscribe_to_notifications,
      address: fullAddress || mapCityName?.search_nm || ''
    };

    try {
      const { data } = await cts_secure('post', `${apis.reminderSignUp}/reminder/weekly/${method}`, payload);
      if (!data || !data.success) {
        throw new ServerError(data.message);
      }
      setPage(PAGE_STATE.SUCCESS);
    } catch (e) {
      if (e instanceof ServerError) {
        setErrorMsg(e.message);
      } else {
        setErrorMsg('An unexpected error occurred! Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {
        page === PAGE_STATE.FORM && (
          <Container>
            {loading && (<WaitScreen />)}
            <FormProvider {...methods}>
              <form action="">
                {
                  formState === FORM_STATE.INPUT && (
                    <div>
                      <ReminderCommMethod
                        has_phone_reminder={has_phone_reminder}
                        has_sms_reminder={has_sms_reminder}
                        setFormattedPhone={setFormattedPhone}
                        setFormattedSms={setFormattedSms}
                        formattedSms={formattedSms}
                        formattedPhone={formattedPhone}
                      />
                      <ReminderTime />
                      <SubscribeToNotifications />
                      <Button aria-label="confirm to set reminder" onClick={onNext}>
                        {_('reminder.create_reminder')}
                      </Button>
                    </div>
                  )
                }
                {
                  formState === FORM_STATE.VERIFY && (

                    <div>
                      <Verification
                        formData={formData}
                        goBack={() => { setFormState(FORM_STATE.INPUT); }}
                        resend={() => requestPin(formData)}
                        setLoading={setLoading}
                      />
                      <ButtonContainer>
                        <Button
                          type="submit"
                          onClick={onSubmit}
                          aria-label="confirm to set reminder"
                        >
                          {_('reminder.create_reminder')}
                        </Button>
                      </ButtonContainer>
                    </div>
                  )
                }
              </form>
            </FormProvider>
            {
              errorMsg && (
                <ErrorContainer>
                  <ErrorText>{errorMsg}</ErrorText>
                </ErrorContainer>
              )
            }

          </Container>
        )
      }
      {
        page === PAGE_STATE.SUCCESS && (
          <ReminderSuccessScreen
            data={{
              day: formData.day,
              hour: formData.hour,
              method: formData.method,
              communication: formData[formData.method],
            }}
          />
        )
      }
    </>
  );
};

export default HOC(WeeklyReminder);
